/* Brand-page GPSR globe. For each <canvas data-brand-globe data-country="IT" data-cobe="…">,
   lazily loads cobe and spins a dark mint globe to face the manufacturer's country, with a
   marker on it. Fills the hover-card flag + country name. Skipped entirely on mobile /
   reduced-motion (the compliance TEXT is always rendered server-side, so nothing is lost). */

const COUNTRY = {
  IT: { name: 'Italy', lat: 42.8, lng: 12.8 },
  ES: { name: 'Spain', lat: 40.2, lng: -3.7 },
  JP: { name: 'Japan', lat: 36.2, lng: 138.2 },
  GB: { name: 'United Kingdom', lat: 54.0, lng: -2.0 },
  FR: { name: 'France', lat: 46.6, lng: 2.2 },
  SE: { name: 'Sweden', lat: 62.0, lng: 15.0 },
  PT: { name: 'Portugal', lat: 39.5, lng: -8.0 },
  SI: { name: 'Slovenia', lat: 46.1, lng: 14.8 },
  NL: { name: 'Netherlands', lat: 52.1, lng: 5.3 },
  DE: { name: 'Germany', lat: 51.1, lng: 10.4 },
  CZ: { name: 'Czech Republic', lat: 49.8, lng: 15.5 },
  LV: { name: 'Latvia', lat: 56.9, lng: 24.6 },
  AT: { name: 'Austria', lat: 47.6, lng: 14.1 },
  CH: { name: 'Switzerland', lat: 46.8, lng: 8.2 },
  BE: { name: 'Belgium', lat: 50.6, lng: 4.6 },
  US: { name: 'United States', lat: 39.8, lng: -98.6 },
  CA: { name: 'Canada', lat: 56.1, lng: -106.3 },
  CN: { name: 'China', lat: 35.9, lng: 104.2 },
  ID: { name: 'Indonesia', lat: -2.5, lng: 118.0 },
  ZA: { name: 'South Africa', lat: -29.0, lng: 24.0 },
  AU: { name: 'Australia', lat: -25.3, lng: 133.8 },
  TW: { name: 'Taiwan', lat: 23.7, lng: 121.0 },
  KR: { name: 'South Korea', lat: 36.5, lng: 127.9 },
  IN: { name: 'India', lat: 22.6, lng: 79.6 },
};

const flagOf = (cc) =>
  /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isMobile = window.matchMedia && window.matchMedia('(max-width: 899px)').matches;

function initOne(canvas) {
  const cc = (canvas.getAttribute('data-country') || '').toUpperCase();
  const geo = COUNTRY[cc];
  const stage = canvas.closest('.bgpsr__stage');

  // Fill flag + full country name everywhere (works even without WebGL)
  const flag = flagOf(cc);
  const name = geo ? geo.name : cc;
  if (stage) {
    stage.querySelectorAll('[data-bgpsr-flag], [data-bgpsr-flag2]').forEach((el) => (el.textContent = flag));
    stage.parentElement?.querySelectorAll('[data-bgpsr-flag2]').forEach((el) => (el.textContent = flag));
  }
  document.querySelectorAll('[data-bgpsr-flag2]').forEach((el) => (el.textContent = flag));
  document.querySelectorAll('[data-bgpsr-country], [data-bgpsr-country2]').forEach((el) => (el.textContent = name));

  if (!geo || isMobile || reduce) return; // text-only fallback

  const cobeUrl = canvas.getAttribute('data-cobe');
  if (!cobeUrl) return;

  const latRad = (geo.lat * Math.PI) / 180;
  const lngRad = (geo.lng * Math.PI) / 180;
  // Spin so the country sits front-centre: theta = latitude brings the parallel to the
  // vertical centre, so the cobe marker lands under the centred pulse-pin.
  let phi = -lngRad - Math.PI / 2;
  let theta = clamp(latRad, -1.0, 1.0);
  let drag = null;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const size = () => canvas.offsetWidth || 360;

  import(cobeUrl)
    .then((mod) => {
      const createGlobe = mod.default;
      const globe = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: size() * dpr,
        height: size() * dpr,
        phi,
        theta,
        dark: 1,
        diffuse: 1.1,
        mapSamples: 13000,
        mapBrightness: 3.4,
        baseColor: [0.11, 0.15, 0.14],
        markerColor: [0.13, 0.86, 0.72],
        glowColor: [0.06, 0.2, 0.17],
        markers: [{ location: [geo.lat, geo.lng], size: 0.09 }],
        onRender: (state) => {
          state.phi = phi;
          state.theta = theta;
          state.width = size() * dpr;
          state.height = size() * dpr;
        },
      });

      // Drag to spin (hide the card while dragging so it doesn't chase the marker)
      canvas.addEventListener('pointerdown', (e) => {
        drag = { x: e.clientX, y: e.clientY, phi, theta };
        stage && stage.classList.add('is-dragging');
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      });
      window.addEventListener('pointermove', (e) => {
        if (!drag) return;
        phi = drag.phi + (e.clientX - drag.x) / 180;
        theta = clamp(drag.theta + (e.clientY - drag.y) / 180, -1.1, 1.1);
      });
      const release = () => { if (drag) { drag = null; stage && stage.classList.remove('is-dragging'); } };
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    })
    .catch(() => {});
}

function boot() {
  const canvases = document.querySelectorAll('canvas[data-brand-globe]');
  canvases.forEach((canvas) => {
    if (!('IntersectionObserver' in window)) { initOne(canvas); return; }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { obs.disconnect(); initOne(canvas); }
      });
    }, { rootMargin: '400px' });
    io.observe(canvas);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
